import { useEffect, useRef } from "react";
import { importLargestPolygon } from "../../utils/polygonImport";
import { startPolygonDraw } from "../../utils/polygonDraw";
import { emptyPolygon } from "../../conts/defaultValues.js";

// Drawing a polygon on the map, or importing one from a file.
// `which` is always "real" (sinistré) or "sim" (menacé).
const usePolygonInput = ({
  mapRef,
  realPolygon,
  simPolygon,
  setRealPolygon,
  setSimPolygon,
  onImported, // called with the imported feature (used to zoom)
}) => {
  const fileInputRef = useRef(null);
  const importTarget = useRef(null); // which polygon the file is for
  const drawRef = useRef(null); // the running draw session, if any

  // lets the draw callbacks read the latest polygons
  const polygonsRef = useRef({});
  polygonsRef.current = { real: realPolygon, sim: simPolygon };

  const setterFor = (which) => (which === "real" ? setRealPolygon : setSimPolygon);

  /* -------------------------------- draw -------------------------------- */
  // stop drawing when the template unmounts
  useEffect(() => () => drawRef.current?.stop(), []);

  const stopDraw = () => {
    drawRef.current?.stop();
    drawRef.current = null;
  };

  const handleDraw = (which) => {
    const map = mapRef.current;
    if (!map) return;
    const set = setterFor(which);

    // if we restart a draw on the same polygon, keep the original "previous"
    const previous =
      drawRef.current?.which === which
        ? drawRef.current.previous
        : polygonsRef.current[which];

    drawRef.current?.stop();
    set({ ...previous, drawing: true, vertices: 0 });

    const session = startPolygonDraw(map, {
      onProgress: (vertices) => set((p) => ({ ...p, vertices })),
      onComplete: ({ feature, area, vertices }) => {
        drawRef.current = null;
        set({ ...emptyPolygon, feature, area, vertices });
      },
      onCancel: () => {
        drawRef.current = null;
        set(previous);
      },
    });

    drawRef.current = { which, previous, stop: session.cancel };
  };

  /* ------------------------------- import ------------------------------- */
  const handleImport = (which) => {
    stopDraw(); // a running draw would otherwise overwrite the imported polygon
    importTarget.current = which;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // lets the user pick the same file again
    if (!file) return;

    const set = setterFor(importTarget.current);
    try {
      const { feature, area, vertices } = await importLargestPolygon(file);
      set({ ...emptyPolygon, feature, area, vertices });
      onImported?.(feature);
    } catch (err) {
      alert(err.message);
    }
  };

  return { fileInputRef, handleImport, handleFileChange, handleDraw, stopDraw };
};

export default usePolygonInput;
